import { motion } from 'framer-motion';
import CardBackArt from 'components/CardBackArt';
import { isValidCardCode, parseCardCode } from 'lib/card-utils';

function SuitMark({ suit, red }: { suit: string; red: boolean }) {
  return (
    <span
      className={`tp-card-suit ${red ? 'tp-card-suit-red' : 'tp-card-suit-black'}`}
      aria-hidden
    >
      {suit}
    </span>
  );
}

interface PlayingCardProps {
  code: string;
  hidden?: boolean;
  delay?: number;
  small?: boolean;
  medium?: boolean;
  large?: boolean;
  compact?: boolean;
  fanIndex?: number;
  flatLayout?: boolean;
  animate?: 'deal' | 'flip' | 'settle' | 'none';
  roundKey?: number;
}


function fanSlotClass(index: number, faceUp: boolean): string {
  const idx = Math.min(2, Math.max(0, index));
  return `tp-card-fan-slot tp-card-fan-classic tp-card-fan-idx-${idx}${faceUp ? ' tp-card-fan-slot-open' : ' tp-card-fan-slot-closed'}`;
}

function CardBack() {
  return (
    <div className="tp-card-back absolute inset-0 overflow-hidden rounded-[inherit]">
      <CardBackArt />
    </div>
  );
}

/** Face-up — top-left rank with suit below */
function CardCorner({ rank, suit, red }: { rank: string; suit: string; red: boolean }) {
  return (
    <div className="tp-card-corner tp-card-corner-tl" aria-hidden>
      <span className={`tp-card-rank ${red ? 'tp-card-rank-red' : 'tp-card-rank-black'}`}>{rank}</span>
      <SuitMark suit={suit} red={red} />
    </div>
  );
}

function CardFace({ rank, suit, red }: { rank: string; suit: string; red: boolean }) {
  const suitClass = red ? 'tp-card-suit-red' : 'tp-card-suit-black';
  return (
    <div className="tp-card-face absolute inset-0 rounded-[inherit]">
      <div className="tp-card-face-paper" aria-hidden />
      <div className="tp-card-face-shine" aria-hidden />
      <div className="tp-card-face-frame" aria-hidden />
      <CardCorner rank={rank} suit={suit} red={red} />
      <div className="tp-card-face-center" aria-hidden>
        <span className={`tp-card-face-watermark ${suitClass}`}>{suit}</span>
      </div>
    </div>
  );
}

export default function PlayingCard({
  code,
  hidden,
  delay = 0,
  small,
  medium,
  large,
  compact,
  fanIndex = 1,
  flatLayout = false,
  animate = 'settle',
  roundKey = 0,
}: PlayingCardProps) {
  const faceUp = !hidden && code !== 'back' && isValidCardCode(code);
  const parsed = faceUp ? parseCardCode(code) : null;
  const { rank = '', suit = '', red = false } = parsed ?? {};
  const size = compact
    ? large
      ? 'tp-card-size-hero'
      : medium || small
        ? 'tp-card-size-opp'
        : 'tp-card-size-opp'
    : large
      ? 'h-[88px] w-[62px] sm:h-[96px] sm:w-[68px]'
      : small
        ? 'h-[58px] w-[40px] sm:h-[64px] sm:w-[44px]'
        : medium
          ? 'h-[72px] w-[50px] sm:h-[78px] sm:w-[54px]'
          : 'h-[79px] w-[56px] lg:h-[86px] lg:w-[61px]';

  const flipY = 0;
  const baseTransition = { delay, type: 'spring' as const, stiffness: 260, damping: 20 };

  let initial: Record<string, number> = { rotateY: flipY, y: 0, scale: 1, opacity: 1 };
  let animateTo: Record<string, number> = { rotateY: flipY, y: 0, scale: 1, opacity: 1 };
  let transition: Record<string, unknown> = { ...baseTransition, duration: 0.4 };

  if (animate === 'deal') {
    initial = { rotateY: 180, y: -36, scale: 0.4, opacity: 0 };
    animateTo = { rotateY: flipY, y: 0, scale: 1, opacity: 1 };
    transition = { ...baseTransition, duration: 0.55 };
  } else if (animate === 'flip' && faceUp) {
    initial = { rotateY: 180, y: 0, scale: 0.92, opacity: 1 };
    animateTo = { rotateY: 0, y: 0, scale: 1, opacity: 1 };
    transition = { ...baseTransition, duration: 0.65, stiffness: 200, damping: 16 };
  } else if (animate === 'settle') {
    initial = { rotateY: flipY, y: 12, scale: 0.85, opacity: 0 };
    animateTo = { rotateY: flipY, y: 0, scale: 1, opacity: 1 };
  }

  if (animate === 'none') {
    initial = { rotateY: flipY, y: 0, scale: 1, opacity: 1 };
    animateTo = initial;
  }


  const cardMotion = (
    <motion.div
      key={`${roundKey}-${code}-${fanIndex}-${faceUp ? 'f' : 'b'}`}
      initial={initial}
      animate={animateTo}
      transition={transition}
      style={{ transformStyle: 'preserve-3d' }}
      className={`${size} tp-playing-card tp-card-premium relative shrink-0 overflow-hidden${faceUp ? ' tp-card-face-up' : ' tp-card-back-up'}`}
    >
      {faceUp && (
        <div
          className="absolute inset-0 rounded-[inherit]"
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(0deg)' }}
        >
          <CardFace rank={rank} suit={suit} red={red} />
        </div>
      )}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: faceUp ? 'rotateY(180deg)' : 'rotateY(0deg)',
          visibility: faceUp ? 'hidden' : 'visible',
        }}
      >
        <CardBack />
      </div>
    </motion.div>
  );

  if (flatLayout) {
    return cardMotion;
  }

  return (
    <div className={fanSlotClass(fanIndex, faceUp)}>
      {cardMotion}
    </div>
  );
}

export function CardBackShell({
  compact,
  small,
  medium,
  className = '',
}: {
  compact?: boolean;
  small?: boolean;
  medium?: boolean;
  className?: string;
}) {
  const sizeClass = compact || medium || small ? 'tp-card-size-opp' : 'tp-card-size-hero';
  return (
    <div className={`${sizeClass} tp-playing-card tp-card-premium tp-card-back-up relative shrink-0 ${className}`}>
      <CardBack />
    </div>
  );
}
